import { useNotifyAlert } from 'context/NotifyAlert';
import { useMessage } from 'newComponents/ChatWhatsapp/context/MessageContext';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Image as ImageIcon, Description as DescriptionIcon, Audiotrack as AudioIcon, EmojiEmotions, Star, StarBorder } from '@material-ui/icons';
import { formatMessageTime } from '../../utils/dateFormatters';
import { MessageStatus } from '../ChatArea/components/MessageStatus';
import { ChatListItem as ChatListItemComponent, DropdownMenu, DropdownItem, DropdownOverlay, ChatListUnreadCount, ChatListUnreadCountContainer, MessageTimeText, PhoneNumberIsActiveBlock, RightSideContainer, OfficialChatBadge } from './styles';
import { AnimatedArrowIcon, ChatContent, ChatListInfo, ChatListMessage, ChatListName, StatusButton, ChatListResponsible, ChatListResponsibleText } from './styles';
import { formatPhoneNumber } from 'util/utils';
import CallIcon from '@material-ui/icons/Call';
import BlockIcon from '@mui/icons-material/Block';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Tooltip } from "@material-ui/core";


export const ChatListItem = ( { conversation } ) => {
	const { setShowNotify } = useNotifyAlert();
	const userEmail = useSelector( state => state.auth.userLogin );
	const { updateChat, handleChatClick, setConversations, selectedChatRef } = useMessage();

	const handleMarkAsPending = async ( conversation ) => {
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode marcá-lo como pendente.'
			} );
			return;
		}

		try {
			await updateChat( { ...conversation, status: 'Pendente' } );
		} catch ( error ) {
			console.error( 'Erro ao marcar conversa como pendente:', error );
		}
	};

	const handleMarkAsOpen = async ( conversation ) => {
		// Verifica se o usuário atual é o responsável pelo chat
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode marcá-lo como em aberto.'
			} );
			return;
		}

		try {
			await updateChat( { ...conversation, status: 'Em aberto' } );
		} catch ( error ) {
			console.error( 'Erro ao marcar conversa como em aberto:', error );
		}
	};

	const handleStatusChange = ( conversation ) => {
		// Verifica se o usuário atual é o responsável pelo chat
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode alterar seu status.'
			} );
			return;
		}

		if ( conversation?.status === 'Pendente' ) {
			handleMarkAsOpen( conversation );
		} else {
			handleMarkAsPending( conversation );
		}
	};

	const handleToggleFavorite = async ( conversation ) => {
		if ( conversation?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode alterar seus favoritos.'
			} );
			return;
		}

		try {
			const isFavorite = conversation?.isFavorite || false;
			setConversations( prevConversations => {
				return prevConversations.map( conv => {
					if ( conv.id === conversation?.id ) {
						return { ...conv, isFavorite: !isFavorite };
					}
					return conv;
				} );
			} );
			setShowNotify( {
				status : 'success',
				text   : isFavorite ? 'Chat removido dos favoritos' : 'Chat adicionado aos favoritos'
			} );
			await updateChat( { ...conversation, isFavorite: !isFavorite } );
		} catch ( error ) {
			console.error( 'Erro ao alterar favorito:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao alterar favorito'
			} );
		}
	};

	// Chats oficiais trazem o status da última mensagem: mesmo ícone da conversa (relógio, check
	// simples, cinza, azul ou erro). Sem o status (bailey), mantém o check azul de antes.
	const SentPrefix = () => (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginRight: '2px', flexShrink: 0 }}>
			{conversation?.lastMessageStatus != null ? (
				<MessageStatus status={conversation.lastMessageStatus} />
			) : (
				<svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 5.5L4.5 9L11 2" stroke="#53bdeb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
					<path d="M5 5.5L8.5 9L15 2" stroke="#53bdeb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			)}
		</span>
	);

	const renderMessageContent = ( conversation ) => {
		const fromMe = conversation?.lastMessageFromMe || conversation?.fromLastMessage;

		if ( !conversation?.content && conversation?.mediaType === 'text' && !fromMe ) return '';

		if ( conversation?.mediaType === 'image' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{fromMe && <SentPrefix />}
					<ImageIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Imagem</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'document' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{fromMe && <SentPrefix />}
					<DescriptionIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Documento</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'audio' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{fromMe && <SentPrefix />}
					<AudioIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>Áudio</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'call') {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<CallIcon style={{ fontSize: '16px', color: '#8696a0' }} />
					<span>{conversation?.content}</span>
				</div>
			);
		}

		if ( conversation?.lastMessageReactionEmoji ) {
			const reactedText = conversation?.content?.length > 30
				? `${conversation.content.substring( 0, 30 )}...`
				: conversation?.content;
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{reactedText
						? <span>Reagiu {conversation.lastMessageReactionEmoji} a &quot;{reactedText}&quot;</span>
						: <span>Reagiu {conversation.lastMessageReactionEmoji} a uma mensagem</span>}
				</div>
			);
		}

		if ( conversation?.mediaType === 'template' ) {
			const text = conversation?.content?.length > 30
				? `${conversation?.content?.substring( 0, 30 )}...`
				: conversation?.content;
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
					{fromMe && <SentPrefix />}
					<span>{text || 'Modelo enviado'}</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'reaction' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<span>{conversation?.content || '🙂'}</span>
					<span>Reagiu a uma mensagem</span>
				</div>
			);
		}

		if ( conversation?.mediaType === 'sticker' ) {
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					{fromMe && <SentPrefix />}
					<EmojiEmotions style={{ fontSize: '16px', color: '#8696a0' }} />
					{conversation?.storageUrl && (
						<img src={conversation.storageUrl} alt="Sticker" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', background: 'transparent' }} />
					)}
					<span>Figurinha</span>
				</div>
			);
		}

		// Para mensagens de texto
		const text = conversation?.content?.length > 30
			? `${conversation?.content?.substring( 0, 30 )}...`
			: conversation?.content;

		if ( !text ) {
			if ( fromMe ) return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
					<SentPrefix />
					<span>Mensagem enviada</span>
				</div>
			);
			return '';
		}

		return (
			<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
				{fromMe && <SentPrefix />}
				<span>{text}</span>
			</div>
		);
	};

	const [ isMenuOpen, setIsMenuOpen ] = useState( false );
	const [ isHovered, setIsHovered ] = useState( false );

	const toggleMenu = ( e ) => {
		e.stopPropagation(); // Impedir que o clique na flecha dispare o clique no item de conversa
		setIsMenuOpen( ( prev ) => !prev );
	};

	const closeMenu = () => {
		setIsMenuOpen( false );
	};

	const handleFavoriteClick = ( e ) => {
		e.stopPropagation();
		handleToggleFavorite( conversation );
		closeMenu();
	};

	return (
		<>
			<DropdownOverlay isOpen={isMenuOpen} onClick={closeMenu} />
			<ChatListItemComponent
				onClick={() => handleChatClick( conversation )}
				style={{ cursor: 'pointer', position: 'relative', border: '1px solid #red' }}
				data-ended={conversation?.status?.toLowerCase() === 'finalizado'}
				data-pending={conversation?.status === 'Pendente'}
				onMouseEnter={() => setIsHovered( true )}
				onMouseLeave={() => setIsHovered( false )}
				selected={conversation?.id === selectedChatRef?.current?.id}
			>
				{!conversation?.phoneNumberIsActive && (
						<PhoneNumberIsActiveBlock isOpen={!conversation?.phoneNumberIsActive}>
							<Tooltip title="Atendida por telefone desativado">
								<BlockIcon style={{ fontSize: '12px', color: '#f04545' }} />
							</Tooltip>
						</PhoneNumberIsActiveBlock>
					)}
				<ChatListInfo>
					<ChatContent className="chat-content">
						<div style={{
							display        : 'flex',
							justifyContent : 'space-between',
							width          : '100%',
							marginBottom   : '4px'
						}}>
							<ChatListName>
								<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
									{conversation?.name || formatPhoneNumber(conversation?.id.split( '@' )[0])}
									{conversation?.source === 'oficial' && (
										<Tooltip title="Conversa através do número oficial">
											<VerifiedIcon style={{ fontSize: '14px', color: '#25D366' }} />
										</Tooltip>
									)}
								</div>
							</ChatListName>
						</div>
						{( conversation?.userId || conversation?.userEmail ) && (
							<ChatListResponsible>
								<ChatListResponsibleText>
									{conversation?.userName || conversation?.userEmail}
									{conversation?.departmentName ? ` - ${conversation?.departmentName}` : ''}
								</ChatListResponsibleText>
							</ChatListResponsible>
						)}
						<div style={{
							display        : 'flex',
							justifyContent : 'space-between',
							width          : '100%'
						}}>
							<ChatListMessage>
								<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
									{renderMessageContent( conversation )}
								</div>
							</ChatListMessage>

						</div>
					</ChatContent>
				</ChatListInfo>
				<RightSideContainer>
					<MessageTimeText>
						{formatMessageTime( conversation?.createdAt )}
					</MessageTimeText>
					<ChatListUnreadCountContainer>
						{conversation?.unreadCount > 0 ? (
							<ChatListUnreadCount>
								{conversation?.unreadCount}
							</ChatListUnreadCount>
						) : conversation?.status !== 'Finalizado' && ( !conversation?.unreadCount || conversation?.unreadCount === 0 ) && conversation?.userId ? (
							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<StatusButton
									className="status-button"
									status={conversation?.status}
									onClick={( e ) => {
										e.stopPropagation();
										handleStatusChange( conversation );
									}}
									title={conversation?.status === 'Pendente' ? 'Remover pendência' : 'Marcar como pendente'}
									>
								</StatusButton>
							</div>
						) : null}
						<AnimatedArrowIcon
							onClick={toggleMenu}
							isHovered={isHovered}
							/>
					</ChatListUnreadCountContainer>
				</RightSideContainer>

				<DropdownMenu isOpen={isMenuOpen}>
					<DropdownItem onClick={handleFavoriteClick}>
						{conversation?.isFavorite ? (
							<>
								<StarBorder />
								<span>Remover dos favoritos</span>
							</>
						) : (
							<>
								<Star />
								<span>Adicionar aos favoritos</span>
							</>
						)}
					</DropdownItem>
				</DropdownMenu>
			</ChatListItemComponent>
		</>
	);
};
